import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

interface CapturedImage {
  id: string;
  file: File;
  preview: string;
}

export const BottleAddPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [images, setImages] = useState<CapturedImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newImages: CapturedImage[] = [];
    
    Array.from(files).forEach((file) => {
      if (images.length + newImages.length >= 3) return;
      
      if (file.type.startsWith('image/')) {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        const preview = URL.createObjectURL(file);
        newImages.push({ id, file, preview });
      }
    });

    setImages(prev => [...prev, ...newImages]);
    setError(null);
  };

  const removeImage = (id: string) => {
    setImages(prev => {
      const updated = prev.filter(img => img.id !== id);
      const imageToRemove = prev.find(img => img.id === id);
      if (imageToRemove) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return updated;
    });
  };

  const uploadAndCreate = async () => {
    if (images.length === 0) {
      setError('写真を撮影してください');
      return;
    }

    if (!user?.store_id) {
      setError('店舗情報が取得できません');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // 1. Presigned URL取得
      const filesInfo = images.map(img => ({
        filename: img.file.name || `image_${img.id}.jpg`,
        content_type: img.file.type,
        size: img.file.size
      }));

      const presignedResponse = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/images/upload`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
          },
          body: JSON.stringify({ files: filesInfo }),
        }
      );

      if (!presignedResponse.ok) {
        throw new Error('Presigned URL取得に失敗しました');
      }

      const presignedData = await presignedResponse.json();
      const uploadUrls = presignedData.data.upload_urls;

      // 2. S3に直接アップロード
      const uploadPromises = images.map(async (image, index) => {
        const uploadInfo = uploadUrls[index];
        
        const uploadResponse = await fetch(uploadInfo.upload_url, {
          method: 'PUT',
          headers: {
            'Content-Type': image.file.type,
          },
          body: image.file,
        });

        if (!uploadResponse.ok) {
          throw new Error(`画像${index + 1}のアップロードに失敗しました`);
        }

        return `s3://${uploadInfo.bucket_name}/${uploadInfo.key}`;
      });

      const imageUrls = await Promise.all(uploadPromises);

      // 3. ボトル作成（解析は非同期で実行される）
      const createResponse = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stores/${user.store_id}/bottles`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('idToken')}`,
          },
          body: JSON.stringify({
            image_urls: imageUrls,
            bottle_type: 'whisky', // デフォルト値
            producer: '',
            country: ''
          }),
        }
      );

      if (!createResponse.ok) {
        throw new Error('ボトルの作成に失敗しました');
      }

      const createResult = await createResponse.json();
      console.log('Bottle created:', createResult);
      
      // 即座に一覧画面へ遷移
      navigate('/bottles', {
        state: {
          message: 'アップロードが完了しました。解析結果は一覧画面で確認できます。'
        }
      });

    } catch (err: any) {
      console.error('Upload/Create error:', err);
      setError(err.message || '処理中にエラーが発生しました');
      setIsUploading(false);
    }
  };

  const canAddMore = images.length < 3;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">ボトル追加</h1>
            <button
              onClick={() => navigate('/bottles')}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {/* 撮影画像プレビュー */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">撮影画像 ({images.length}/3)</h2>
            
            {images.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                {images.map((image) => (
                  <div key={image.id} className="relative">
                    <img
                      src={image.preview}
                      alt="撮影画像"
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                    <button
                      onClick={() => removeImage(image.id)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* カメラ撮影ボタン */}
            {canAddMore && (
              <button
                onClick={handleCameraCapture}
                disabled={isUploading}
                className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50"
              >
                <div className="text-3xl mb-2">📷</div>
                <div className="text-sm">写真を撮影</div>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* エラーメッセージ */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* 処理状況表示 */}
          {isUploading && (
            <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">
              画像をアップロード中...
            </div>
          )}

          {/* アップロードボタン */}
          <button
            onClick={uploadAndCreate}
            disabled={images.length === 0 || isUploading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isUploading ? 'アップロード中...' : 'アップロード'}
          </button>

          {/* 使用方法 */}
          <div className="mt-6 text-sm text-gray-600">
            <h3 className="font-semibold mb-2">使用方法:</h3>
            <ul className="space-y-1">
              <li>• ボトルの写真を1〜3枚撮影してください</li>
              <li>• ラベルが鮮明に写るように撮影してください</li>
              <li>• アップロード後、一覧画面で解析結果を確認できます</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
